import { CREDIT_LIMITS, CREDIT_MEANING, CREDIT_READ_AGAIN } from "./credit-words.ts";
import type { Words } from "./site-words.ts";

// The site's words in English (S10.02).

export const EN: Words = {
  tagline: "AI that runs your business",
  signIn: "Sign in",
  signUp: "Sign up",
  demoLabel: "Try it",
  howItWorksLabel: "How it works",
  howItWorksTitle: "From an audience to a short list",
  howItWorksDescription: "Three steps, one workspace. No spreadsheets in between.",
  productLabel: "Product",
  productTitle: "Everything after the search, too",
  productDescription:
    "Finding leads is the start. {product} reads every contact, fills in what it can and keeps your short list in one place, ready to export.",
  faqLabel: "FAQ",
  faqTitle: "Questions, answered",
  homeTitle: "{product} · Instagram leads with public contacts",
  brandTitle: "Brand & press · {product}",
  notFoundTitle: "Page not found · {product}",
  homeDescription:
    "Find Instagram leads with a public email or phone, from a competitor's followers or the accounts it follows.",
  homeKeyphrase: "Instagram leads",
  brandDescription: "Logos, colours and the words for writing about {product}, ready to download.",
  heroEyebrow: "All-in-one AI prospecting",
  heroTitleLead: "Fresh Instagram leads",
  heroTitleTail: "from",
  heroSources: ["@competitor", "followers", "following"],
  platforms: {
    instagram: "Instagram",
    x: "X.com",
    googleMaps: "Google Maps",
    tiktok: "TikTok",
    linkedin: "LinkedIn",
  },
  heroSourcesLabel: "a competitor's account, its followers or the accounts it follows",
  heroLedeLead: "Competitors and other accounts your buyers already follow.",
  heroLedeTail: " reads that audience and leaves it ready for you.",
  heroStart: "Get started free",
  heroNote: "No credit card. Your first analysis runs on free credits.",
  analyses: "Analyses",
  demoPlaceholder: "@an account your buyers follow",
  demoAction: "Read the audience",
  demoReading: "Reading the audience…",
  demoHandleLabel: "Account handle",
  demoFollowersRead: "Followers read",
  demoBusinesses: "Businesses",
  demoWithEmail: "With a public email",
  demoTableLabel: "Leads in the audience of @acme_company",
  demoShowing: "Showing 1–{shown} of {total}",
  demoCaption:
    "A preview with made-up businesses and masked contacts: the real thing reads public profiles, emails included. Open any lead.",
  demoPlaces: {
    businesses: [
      {
        kind: "Interior design studio",
        bio: "Calm, light-filled homes. Residential projects across the city.",
      },
      { kind: "Organic grocer", bio: "Local, seasonal and organic. Fresh boxes delivered on Fridays." },
      { kind: "Physiotherapy clinic", bio: "Sports injuries and rehab. Book your first assessment online." },
      { kind: "Pilates studio", bio: "Reformer classes for every level. Small groups, big results." },
      { kind: "Florist", bio: "Wedding and event florals, plus weekly flowers for offices." },
      {
        kind: "Specialty coffee roaster",
        bio: "Single-origin beans roasted in small batches. Wholesale welcome.",
      },
      { kind: "Bridal boutique", bio: "Made-to-measure bridal wear. Fittings by appointment only." },
      { kind: "Sports nutrition shop", bio: "Supplements and plans for runners, riders and lifters." },
      { kind: "Dental practice", bio: "Family dentistry and whitening. New patients this month." },
      { kind: "Bakery", bio: "Sourdough, pastries and custom cakes, baked every morning." },
      { kind: "Yoga studio", bio: "Vinyasa, yin and prenatal yoga. First class is on us." },
      { kind: "Barbershop", bio: "Classic cuts and hot-towel shaves. Walk-ins until 6pm." },
    ],
    cities: ["Madrid", "Valencia", "Lisbon", "Amsterdam", "Milan", "Berlin", "Barcelona", "Porto"],
  },
  columns: {
    name: "Name & username",
    email: "Email",
    phone: "Phone",
    followers: "Followers",
    website: "Website",
  },
  detail: {
    close: "Close lead",
    fit: "Fit",
    email: "Public email",
    phone: "Phone",
    website: "Website",
    business: "Business",
    followers: "Followers",
    following: "Following",
  },
  defaultProject: "Default Project",
  newAnalysis: "New analysis",
  nav: {
    dashboard: "Dashboard",
    analyses: "Analyses",
    prospects: "Prospects",
  },
  journey: [
    {
      eyebrow: "1 · Find",
      title: "Point at an audience",
      description:
        "The followers of an account, or the accounts it follows. {product} reads the public profiles and leaves out the private ones.",
    },
    {
      eyebrow: "2 · Qualify",
      title: "Keep the ones you can reach",
      description:
        "Public emails and phones, read from each public profile. Search and sort by niche, size and city to turn thousands into a short list.",
    },
    {
      eyebrow: "3 · Use",
      title: "Take your list anywhere",
      description:
        "Export it to a spreadsheet or your CRM, or enrich a lead with more of what it publishes. Every contact keeps a note of where it came from.",
    },
  ],
  features: [
    {
      title: "Every platform, one search",
      description:
        "Instagram followers and following today; X, Google Maps, TikTok and LinkedIn are coming soon.",
    },
    {
      title: "Where your contacts come from",
      description: "Every contact comes from the public profile, or a likely email guessed from its website.",
    },
    {
      title: "Enrich what matters",
      description:
        "Turn on auto-enrich and {product} fills in a lead's website, category and links as it finds them.",
    },
    {
      title: "Export in one click",
      description: "Download the whole list or a selection as CSV, ready for a spreadsheet or your CRM.",
    },
    {
      title: "Works from Claude",
      description: "Ask Claude to analyse an account or sum up your leads, and it happens in your workspace.",
    },
  ],
  stats: [
    { value: "1 account", label: "is all it takes to start" },
    { value: "< 2 min", label: "from a handle to a short list" },
    { value: "1 credit", label: "reads 1 public Instagram profile" },
  ],
  questions: [
    {
      question: "Where do the contacts come from?",
      answer: `Public Instagram profiles, of businesses and people: the email, phone and website each one publishes. ${CREDIT_LIMITS.en} Every contact keeps a note of where it came from.`,
    },
    {
      question: "Which platforms can I read?",
      answer:
        "Instagram today: an account's followers and the accounts it follows. X, Google Maps, TikTok and LinkedIn are coming soon. Tell us what you need and we will prioritise it.",
    },
    {
      question: "How are credits counted?",
      answer: `${CREDIT_MEANING.en} ${CREDIT_READ_AGAIN.en} Your first analysis runs on free credits, and you can set a limit on any analysis.`,
    },
    {
      question: "When can I use it?",
      answer: "Today. Create an account and your first analysis runs on free credits.",
    },
  ],
  ctaTitle: "Your short list is one handle away",
  ctaDescription: "Name an account your buyers follow and get your first leads in minutes.",
  footer: {
    product: "Product",
    features: "Features",
    company: "Company",
    brand: "Brand & press",
    contact: "Contact",
    resources: "Resources",
  },
  legal: "© 2026 {product}. All rights reserved.",
  notFound: {
    trail: "Not found",
    addressLabel: "The address you opened",
    tableLabel: "Results for this address",
    title: "This page does not exist",
    emptyTitle: "Nothing found at this address",
    emptyDescription: "Try the home page, or name an account to read its audience.",
    description: "The link may be old, or the address mistyped.",
    home: "Back to the home page",
  },
  cookies: {
    title: "We use cookies",
    body: "We use cookies to analyse traffic and improve your experience. Accept all, keep only what's necessary, or choose which to allow. You can change this any time in Privacy choices at the bottom of every page.",
    reject: "Only necessary",
    accept: "Accept all",
    choose: "Customize",
    choicesTitle: "Privacy choices",
    choicesDescription: "Choose what this site may use. Strictly necessary items are always on.",
    save: "Save choices",
    requiredTitle: "Strictly necessary",
    requiredDescription:
      "Remembers this choice, and keeps you signed in if you use {product}. Not used for tracking.",
    requiredLabel: "Always on",
    analyticsTitle: "Analytics",
    analyticsDescription:
      "Counts visits and connects a visit to a sign-up. When off, your visits are not measured.",
    advertisingTitle: "Advertising",
    advertisingDescription: "Measures and improves our ads. When off, no advertising tool loads.",
  },
};
